import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const NewYearCountdownCalculator = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const newYear = new Date(now.getFullYear() + 1, 0, 1); // January 1st of the next year
      const difference = newYear - now;

      let timeLeft = {};

      if (difference > 0) {
        timeLeft = {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }

      return timeLeft;
    };

    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const chartData = {
    labels: ['Days', 'Hours', 'Minutes', 'Seconds'],
    datasets: [
      {
        label: 'Time Left',
        data: [timeLeft.days, timeLeft.hours, timeLeft.minutes, timeLeft.seconds],
        backgroundColor: ['rgba(255, 99, 132, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(75, 192, 192, 0.5)', 'rgba(153, 102, 255, 0.5)'],
        borderColor: ['rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)', 'rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)'],
        borderWidth: 1
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>New Year Countdown Calculator | Time Until New Year</title>
          <meta name="description" content="Countdown to the next New Year's Day. This calculator helps you track the remaining time until the next January 1st." />
          <meta name="keywords" content="New Year countdown, time until New Year, New Year's Day, New Year calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🎉</div>
            <h1 className="text-3xl font-bold text-gray-800">New Year Countdown Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Countdown to the next New Year's Day. Track the remaining time until January 1st.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Time Until New Year</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{timeLeft.days}</p>
                  <p className="text-gray-600">Days</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{timeLeft.hours}</p>
                  <p className="text-gray-600">Hours</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{timeLeft.minutes}</p>
                  <p className="text-gray-600">Minutes</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{timeLeft.seconds}</p>
                  <p className="text-gray-600">Seconds</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Time Left'
                    }
                  },
                  x: {
                    title: {
                      display: true,
                      text: 'Time Units'
                    }
                  }
                },
                plugins: {
                  title: {
                    display: true,
                    text: 'Time Left Until New Year'
                  }
                }
              }}
            />
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the New Year Countdown Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>View the Countdown:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>The calculator automatically counts down to the next New Year's Day.</li>
                  <li>The remaining days, hours, minutes, and seconds are displayed.</li>
                </ul>
              </li>
              <li><strong>Check the Graph:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>The bar chart provides a visual representation of the remaining time.</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
          <div className="text-gray-600">
            <div className="mb-4">
              <h4 className="text-lg font-semibold">What is a New Year Countdown Calculator?</h4>
              <p>A New Year Countdown Calculator is a tool that helps you track the remaining time until the next New Year's Day.</p>
            </div>
            <div className="mb-4">
              <h4 className="text-lg font-semibold">How accurate is the countdown?</h4>
              <p>The countdown is accurate to the second and updates in real-time.</p>
            </div>
            <div className="mb-4">
              <h4 className="text-lg font-semibold">Can I customize the countdown?</h4>
              <p>Currently, the countdown is set to the next New Year's Day. Customization for other dates may be added in future updates.</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default NewYearCountdownCalculator;